import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import {
  MicrosoftAuthButtonComponent,
  MicrosoftLoginService,
} from '@sia/microsoft-auth';
import {
  EnvironmentService,
  SiaLayoutService,
  SiaTranslatePipe,
  notify,
} from '@sia/shared';
import { AuthComponent } from '../../auth.component';
import { SiaAuthResult } from '../../interfaces/sia-auth-result';

@Component({
  selector: 'app-login',
  templateUrl: './auth-login.component.html',
  styleUrls: ['./auth-login.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    MatFormField,
    MatLabel,
    MatInputModule,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatButton,
    RouterModule,
    MatProgressSpinner,
    SiaTranslatePipe,
    MicrosoftAuthButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthLoginComponent
  extends AuthComponent
  implements OnInit, OnDestroy
{
  #layoutService = inject(SiaLayoutService);
  #microsoftLoginService = inject(MicrosoftLoginService);
  #envService = inject(EnvironmentService);

  credentials = signal<any>({});
  minLength = signal(5);
  maxLength = signal(20);
  loginStarted2FA = signal(false);
  qrCodeString = signal('');
  hide = signal(true);
  twoFactorType = signal('');
  enableOtp = signal(false);

  password = signal('');
  userIdentifier = signal('');

  microsoftLoginAvailable = signal(false);

  @HostBinding('class') hostClasses = 'w-full px-4 lg:px-0';
  @ViewChild('passwordInput') passwordInputRef!: ElementRef<HTMLInputElement>;

  constructor() {
    super();
  }

  async ngOnInit() {
    super.ngOnInit();
    const isTokenExpired = await this.tokenStore.isCurrentTokenExpired();
    if (isTokenExpired) {
      await this.clearSession();
    }
    await this.initializeTranslations();

    // Check Microsoft login availability prima del fallback SSO
    await this.#checkMicrosoftAvailability();

    if (!this.toUpdate()) {
      await this.#tryLoginWithFallback();
    } else {
      const sub = this.pwaService.toUpdate$.subscribe(async (update) => {
        if (!update) {
          await this.#tryLoginWithFallback();
          sub.unsubscribe();
        }
      });
    }
  }

  async #checkMicrosoftAvailability() {
    if (!this.#envService.enableMsal) {
      return;
    }
    try {
      const available = await this.#microsoftLoginService.checkAvailability();
      this.microsoftLoginAvailable.set(available);
    } catch {
      // Microsoft login non disponibile
    }
  }

  async #tryLoginWithFallback() {
    await this.tryTokenLogin();

    // Se il token login non ha funzionato e siamo in modalita input,
    // prova SSO silenzioso Microsoft
    if (this.contentReady() === 'input' && this.microsoftLoginAvailable()) {
      try {
        const rawResult = await this.#microsoftLoginService.ssoSilent();
        const authResult = this.#processRawMicrosoftResult(rawResult);
        if (authResult?.success) {
          this.contentReady.set('auto');
          await this.#layoutService.getAllLayouts();
          await this.loginAfterTokenReceived(authResult);
          notify('Login effettuato. Attendere', 1000, 'success');
        }
      } catch {
        // SSO silenzioso fallito — resta sulla pagina login
      }
    }
  }

  async sendNewPin() {
    try {
      const result: any = await this.authService.getNewPinOtp(
        this.userIdentifier(),
      );
      if (result) {
        notify('Nuovo pin inviato', 1000, 'success');
        this.startCountDown(180);
      }
    } catch {
      // ignore
    }
  }

  async login() {
    if (!this.password() || !this.userIdentifier()) {
      notify('Compila tutti i campi', 2000, 'error');
      return;
    }
    if (this.loginStarted2FA() && this.isOtpInvalid()) {
      notify('Pin errato', 2000, 'error');
      return;
    }

    await this.forceClearSession();

    this.startCountDown(180);
    this.loginStarted.set(true);

    const creds = {
      userIdentifier: this.userIdentifier(),
      password: this.password(),
      otp: this.otp(),
    };
    this.credentials.set(creds);

    try {
      const result: any = await this.authService.login(creds);
      if (result.twoFactorType) {
        this.twoFactorType.set(result.twoFactorType);
        this.loginStarted2FA.set(true);
      } else if (result.success) {
        this.stopCountDown();
        await this.#layoutService.getAllLayouts();
        await this.loginAfterTokenReceived(result);
        notify('Login effettuato. Attendere', 1000, 'success');
      } else {
        this.twoFactorType.set('');
        setTimeout(() => this.passwordInputRef?.nativeElement.focus());
      }
    } catch (error) {
      this.twoFactorType.set('');
      notify('Impossibile effettuare login', 2000, 'error');
      setTimeout(() => this.passwordInputRef?.nativeElement.focus());
    } finally {
      this.loginStarted.set(false);
      if (!this.twoFactorType()) {
        this.loginStarted2FA.set(false);
      }
    }
  }

  async onMicrosoftAuthResult(rawResult: any) {
    try {
      await this.forceClearSession();
      this.routerService.clearRoutes();
      const authResult = this.#processRawMicrosoftResult(rawResult);
      if (authResult?.success) {
        this.contentReady.set('auto');
        await this.#layoutService.getAllLayouts();
        await this.loginAfterTokenReceived(authResult);
        notify('Login effettuato. Attendere', 1000, 'success');
      }
    } catch {
      notify('Impossibile effettuare login con Microsoft', 2000, 'error');
    }
  }

  /**
   * Processa il raw result dal backend Microsoft login e lo converte in SiaAuthResult.
   * Replica la logica che prima era in AuthService.loginMicrosoft / #handleLoginResult.
   */
  #processRawMicrosoftResult(result: any): SiaAuthResult | null {
    if (!result) {
      return null;
    }

    if (result.token) {
      sessionStorage.setItem('instance-identifier', result.identifier);
      this.authService.loggedUser();
      this.tokenStore.setToken(result.token);
      return {
        success: true,
        redirect: '/',
        token: result.token,
        errors: result.errors,
        messages: result.messages,
      };
    }

    if (result.twoFactorType) {
      sessionStorage.setItem('instance-identifier', result.identifier);
      return result;
    }

    notify('Impossibile effettuare login con Microsoft', 2000, 'error');
    return null;
  }

  @HostListener('window:unload')
  override ngOnDestroy() {
    super.ngOnDestroy();
    this.credentials.set(null);
    const form = document.getElementById('authForm');
    if (form) form.remove();
  }

  passwordToggle(event: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();
    this.hide.update((h) => !h);
  }

  onOtpChange(value: string) {
    this.otp.set(value);
    if (!this.isOtpInvalid()) {
      this.enableOtp.set(true);
    }
  }
}
